import { useState, useMemo, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import { useCard, useLesson } from '../../hooks/useApi'
import {
  calculateCoefficient,
  roundAmount,
  type TargetShape,
} from '../../utils/calculator'
import {
  parseIngredientsFromContent,
  parseRecipeMetaFromContent,
  type ParsedSection,
  type ShapeType,
  type OriginalShape,
} from '../../utils/parseIngredients'
import { Calculator, Pencil } from 'lucide-react'
import './CalculatorPage.css'

const shapeLabels: Record<ShapeType, string> = {
  circle: 'Круг',
  square: 'Квадрат',
  rectangle: 'Прямоугольник',
}

const CalculatorPage = () => {
  const { cardId } = useParams<{ cardId: string }>()
  const [searchParams] = useSearchParams()
  const lessonId = searchParams.get('lessonId')
  const lessonIdNumber = lessonId ? Number(lessonId) : null
  useTelegramBackButton(true)

  const cardIdNumber = cardId ? Number(cardId) : null

  // Загружаем данные: через lesson API (если есть lessonId) или card API
  const { data: cardData, isLoading: isCardLoading } = useCard(
    lessonIdNumber ? null : cardIdNumber // не запрашиваем card, если есть lessonId
  )
  const { data: lessonData, isLoading: isLessonLoading } = useLesson(lessonIdNumber)

  const isLoading = lessonIdNumber ? isLessonLoading : isCardLoading

  // Унифицируем данные из обоих источников
  const content = lessonData?.lesson?.content?.content || cardData?.card?.content
  const cardTitle = lessonData?.lesson?.info?.title || cardData?.card?.title || ''
  const cardImage =
    lessonData?.lesson?.content?.slider_images?.[0] ||
    lessonData?.lesson?.info?.icon_url ||
    cardData?.card?.slider_images?.[0] ||
    cardData?.card?.icon_url || ''

  // Парсим мету и ингредиенты из контента
  const recipeMeta = useMemo(() => {
    if (!content)
      return { originalShape: null, originalHeight: null, originalWeight: null }
    return parseRecipeMetaFromContent(content)
  }, [content])

  const sections: ParsedSection[] = useMemo(() => {
    if (!content) return []
    return parseIngredientsFromContent(content)
  }, [content])

  // Редактируемые исходные данные
  const [editingOriginal, setEditingOriginal] = useState(false)
  const [origType, setOrigType] = useState<ShapeType>('circle')
  const [origDiameter, setOrigDiameter] = useState('')
  const [origSideA, setOrigSideA] = useState('')
  const [origSideB, setOrigSideB] = useState('')
  const [origHeight, setOrigHeight] = useState('')

  // Инициализируем из парсинга
  useEffect(() => {
    const s = recipeMeta.originalShape
    if (s) {
      setOrigType(s.type)
      if (s.type === 'circle') setOrigDiameter(String(s.diameter || ''))
      if (s.type === 'square') setOrigSideA(String(s.sideA || ''))
      if (s.type === 'rectangle') {
        setOrigSideA(String(s.sideA || ''))
        setOrigSideB(String(s.sideB || ''))
      }
    }
    if (recipeMeta.originalHeight !== null) {
      setOrigHeight(String(recipeMeta.originalHeight))
    }
  }, [recipeMeta])

  // Эффективная исходная форма (из редактирования)
  const effectiveOriginalShape: OriginalShape | null = useMemo(() => {
    switch (origType) {
      case 'circle':
        return Number(origDiameter) > 0
          ? { type: 'circle', diameter: Number(origDiameter) }
          : null
      case 'square':
        return Number(origSideA) > 0
          ? { type: 'square', sideA: Number(origSideA) }
          : null
      case 'rectangle':
        return Number(origSideA) > 0 && Number(origSideB) > 0
          ? {
              type: 'rectangle',
              sideA: Number(origSideA),
              sideB: Number(origSideB),
            }
          : null
    }
  }, [origType, origDiameter, origSideA, origSideB])

  const effectiveOriginalHeight = Number(origHeight) || 0

  // Форма нового торта
  const [targetType, setTargetType] = useState<ShapeType>('circle')
  const [diameter, setDiameter] = useState('')
  const [sideA, setSideA] = useState('')
  const [sideB, setSideB] = useState('')
  const [targetHeight, setTargetHeight] = useState('')
  const [calculated, setCalculated] = useState(false)

  const targetShape: TargetShape = useMemo(() => {
    switch (targetType) {
      case 'circle':
        return { type: 'circle', diameter: Number(diameter) || 0 }
      case 'square':
        return { type: 'square', sideA: Number(sideA) || 0 }
      case 'rectangle':
        return {
          type: 'rectangle',
          sideA: Number(sideA) || 0,
          sideB: Number(sideB) || 0,
        }
    }
  }, [targetType, diameter, sideA, sideB])

  const coefficient = useMemo(() => {
    if (!effectiveOriginalShape) return 0
    return calculateCoefficient(
      effectiveOriginalShape,
      effectiveOriginalHeight,
      targetShape,
      Number(targetHeight) || 0
    )
  }, [effectiveOriginalShape, effectiveOriginalHeight, targetShape, targetHeight])

  const canCalculate = () => {
    if (!effectiveOriginalShape) return false
    if (effectiveOriginalHeight <= 0) return false
    const h = Number(targetHeight)
    if (!h || h <= 0) return false
    switch (targetType) {
      case 'circle':
        return Number(diameter) > 0
      case 'square':
        return Number(sideA) > 0
      case 'rectangle':
        return Number(sideA) > 0 && Number(sideB) > 0
    }
  }

  const handleCalculate = () => {
    if (canCalculate()) setCalculated(true)
  }

  const handleReset = () => {
    setCalculated(false)
    setDiameter('')
    setSideA('')
    setSideB('')
    setTargetHeight('')
  }

  const originalShapeLabel = () => {
    if (!effectiveOriginalShape) return 'Не определена'
    const s = effectiveOriginalShape
    if (s.type === 'circle') return `Круг Ø${s.diameter} см`
    if (s.type === 'square') return `Квадрат ${s.sideA}×${s.sideA} см`
    return `Прямоугольник ${s.sideA}×${s.sideB} см`
  }

  if (isLoading) {
    return (
      <div className="calculator-page safe-area-inset">
        <div className="calculator-body">
          <div className="calc-loading">Загрузка рецепта...</div>
        </div>
      </div>
    )
  }

  if (!content || sections.length === 0) {
    return (
      <div className="calculator-page safe-area-inset">
        <div className="calculator-body">
          <div className="calc-loading">
            Для этого рецепта калькулятор недоступен
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="calculator-page safe-area-inset">
      <div className="calculator-body">
        {/* Исходный рецепт */}
        <div className="original-recipe">
          {cardImage && <img src={cardImage} alt={cardTitle} />}
          <div className="original-recipe-info">
            <span className="original-recipe-title">{cardTitle}</span>
            <span className="original-recipe-shape">
              {effectiveOriginalShape
                ? `${originalShapeLabel()}${effectiveOriginalHeight > 0 ? `, высота ${effectiveOriginalHeight} см` : ''}`
                : 'Форма не определена'}
            </span>
            {recipeMeta.originalWeight && (
              <span className="original-recipe-weight">
                Вес: ~{recipeMeta.originalWeight} г
              </span>
            )}
          </div>
          <button
            className="edit-original-btn"
            onClick={() => setEditingOriginal(!editingOriginal)}
            title="Изменить исходные данные"
          >
            <Pencil size={16} />
          </button>
        </div>

        {/* Редактирование исходных данных */}
        {editingOriginal && (
          <div className="edit-original-form">
            <h3>Исходный торт</h3>
            <div className="shape-selector">
              {(['circle', 'square', 'rectangle'] as ShapeType[]).map(
                (type) => (
                  <button
                    key={type}
                    className={`shape-btn ${origType === type ? 'active' : ''}`}
                    onClick={() => {
                      setOrigType(type)
                      setOrigDiameter('')
                      setOrigSideA('')
                      setOrigSideB('')
                      setCalculated(false)
                    }}
                  >
                    {shapeLabels[type]}
                  </button>
                )
              )}
            </div>
            <div className="calc-inputs">
              {origType === 'circle' && (
                <label className="calc-field">
                  <span>Диаметр (см)</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={origDiameter}
                    onChange={(e) => {
                      setOrigDiameter(e.target.value)
                      setCalculated(false)
                    }}
                    placeholder="16"
                  />
                </label>
              )}
              {origType === 'square' && (
                <label className="calc-field">
                  <span>Сторона (см)</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={origSideA}
                    onChange={(e) => {
                      setOrigSideA(e.target.value)
                      setCalculated(false)
                    }}
                    placeholder="18"
                  />
                </label>
              )}
              {origType === 'rectangle' && (
                <>
                  <label className="calc-field">
                    <span>Сторона A (см)</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={origSideA}
                      onChange={(e) => {
                        setOrigSideA(e.target.value)
                        setCalculated(false)
                      }}
                      placeholder="12"
                    />
                  </label>
                  <label className="calc-field">
                    <span>Сторона B (см)</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={origSideB}
                      onChange={(e) => {
                        setOrigSideB(e.target.value)
                        setCalculated(false)
                      }}
                      placeholder="32"
                    />
                  </label>
                </>
              )}
              <label className="calc-field">
                <span>Высота (см)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={origHeight}
                  onChange={(e) => {
                    setOrigHeight(e.target.value)
                    setCalculated(false)
                  }}
                  placeholder="8"
                />
              </label>
            </div>
          </div>
        )}

        {/* Если не удалось распарсить и не редактируют */}
        {!effectiveOriginalShape && !editingOriginal && (
          <div className="calc-warning">
            Не удалось определить исходную форму торта. Нажмите на карандаш,
            чтобы ввести данные вручную.
          </div>
        )}

        {/* Форма нового торта */}
        {effectiveOriginalShape && !calculated && (
          <div className="calc-form">
            <h2>Ваш торт</h2>

            <div className="shape-selector">
              {(['circle', 'square', 'rectangle'] as ShapeType[]).map(
                (type) => (
                  <button
                    key={type}
                    className={`shape-btn ${targetType === type ? 'active' : ''}`}
                    onClick={() => {
                      setTargetType(type)
                      setDiameter('')
                      setSideA('')
                      setSideB('')
                    }}
                  >
                    {shapeLabels[type]}
                  </button>
                )
              )}
            </div>

            <div className="calc-inputs">
              {targetType === 'circle' && (
                <label className="calc-field">
                  <span>Диаметр (см)</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={diameter}
                    onChange={(e) => setDiameter(e.target.value)}
                    placeholder="16"
                  />
                </label>
              )}

              {targetType === 'square' && (
                <label className="calc-field">
                  <span>Сторона (см)</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={sideA}
                    onChange={(e) => setSideA(e.target.value)}
                    placeholder="18"
                  />
                </label>
              )}

              {targetType === 'rectangle' && (
                <>
                  <label className="calc-field">
                    <span>Сторона A (см)</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={sideA}
                      onChange={(e) => setSideA(e.target.value)}
                      placeholder="12"
                    />
                  </label>
                  <label className="calc-field">
                    <span>Сторона B (см)</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={sideB}
                      onChange={(e) => setSideB(e.target.value)}
                      placeholder="32"
                    />
                  </label>
                </>
              )}

              <label className="calc-field">
                <span>Высота (см)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={targetHeight}
                  onChange={(e) => setTargetHeight(e.target.value)}
                  placeholder="8"
                />
              </label>
            </div>

            <button
              className="calc-submit"
              disabled={!canCalculate()}
              onClick={handleCalculate}
            >
              <Calculator size={18} />
              Рассчитать
            </button>
          </div>
        )}

        {/* Результат */}
        {calculated && (
          <div className="calc-result">
            <div className="coefficient-badge">
              Коэффициент: <strong>×{coefficient.toFixed(2)}</strong>
            </div>

            {recipeMeta.originalWeight && (
              <div className="weight-badge">
                Примерный вес: <strong>~{Math.round(recipeMeta.originalWeight * coefficient)} г</strong>
                <span className="weight-original"> (исходный: {recipeMeta.originalWeight} г)</span>
              </div>
            )}

            {sections.map((section) => (
              <div key={section.name} className="result-section">
                <h3>{section.name}</h3>
                {!section.scalable && (
                  <span className="no-scale-badge">Без пересчёта</span>
                )}
                <div className="ingredients-table">
                  {section.ingredients.map((ing, i) => {
                    const newAmount =
                      section.scalable && ing.amount !== null
                        ? ing.amount * coefficient
                        : null
                    return (
                      <div key={i} className="ingredient-row">
                        <span className="ing-name">{ing.name}</span>
                        <span className="ing-amount">
                          {newAmount !== null ? (
                            <>
                              <span className="ing-original">
                                {ing.amount} {ing.unit}
                              </span>
                              {' → '}
                              <strong>
                                {roundAmount(newAmount)} {ing.unit}
                              </strong>
                            </>
                          ) : (
                            <span>{ing.unit || '—'}</span>
                          )}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}

            <p className="calc-hint">
              Для крема и ганаша рекомендуется добавить +5–10% к рассчитанному
              количеству, особенно для прямоугольных форм.
            </p>

            <button className="calc-reset" onClick={handleReset}>
              Пересчитать заново
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default CalculatorPage
